async function bearer(
  url: string,
  idToken: string,
  init: {
    method?: string;
    headers?: Record<string, string>;
    body?: unknown;
  } = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
    ...(init.headers ?? {}),
  };
  const reqInit: RequestInit = {};
  if (init.method !== undefined) reqInit.method = init.method;
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    reqInit.body = JSON.stringify(init.body);
  }
  reqInit.headers = headers;
  return fetch(url, reqInit);
}

export interface HotelDTO {
  readonly id: string;
  readonly name: string;
  readonly city?: string;
  readonly address?: string;
  readonly phone?: string;
  readonly status: "active" | "disabled";
}

export interface DepartmentDTO {
  readonly id: string;
  readonly code: string;
  readonly displayName: string;
  readonly shifts: ReadonlyArray<"M" | "P" | "N" | "X">;
  readonly status: "active" | "disabled";
  /** Set by `lib/seed-tenant.ts` at self-service signup; the onboarding
   *  wizard's "Reparti" step un-sets it (PATCH `sample: false`) on every
   *  department the owner keeps. Absent on departments created normally. */
  readonly sample?: boolean;
}

export interface TenantSettingsUpdate {
  readonly sessionDuration?: {
    readonly mobile?: string;
    readonly desktop?: string;
  };
  /** Tenant-level notification settings (owner-only). Always sent in full:
   *  the server replaces the whole `notifications` field, so omit no sub-field. */
  readonly notifications?: {
    readonly enabled: boolean;
    readonly channels: { readonly email: boolean; readonly push: boolean };
    readonly rules?: import("@praesto/shared-types").TenantNotificationSettings["rules"];
  };
  /** Set by the onboarding wizard's last step (apps/web/src/onboarding/
   *  OnboardingWizard.tsx) — always `"completed"` from the client; `"pending"`
   *  is only ever written server-side by `provisionTenant`. */
  readonly onboarding?: { readonly status: "completed" };
}

/**
 * Thrown by `updateTenantSettings` on a non-2xx response. Carries the API's
 * `error` code (e.g. `email_locked_governed_users`) and human `message` when
 * the server sent a JSON body, so callers (e.g. NotificationsSettings' tenant
 * gate) can show the owner something actionable instead of a generic failure
 * — this matters most for the 403 email lock, where "error" alone leaves the
 * owner unable to tell a real block from a network blip.
 */
export class TenantSettingsUpdateError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(message: string, status: number, code: string | undefined) {
    super(message);
    this.name = "TenantSettingsUpdateError";
    this.status = status;
    this.code = code;
  }
}

/** Owner-only update of the tenant settings doc (`/tenants/{tid}/settings/general`). */
export async function updateTenantSettings(
  apiBase: string,
  input: TenantSettingsUpdate,
  idToken: string,
): Promise<{ ok: true }> {
  const res = await bearer(`${apiBase}/api/organization/settings/general`, idToken, {
    method: "PUT",
    body: input,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: string;
      message?: string;
    } | null;
    throw new TenantSettingsUpdateError(
      body?.message ?? `updateTenantSettings failed: ${res.status}`,
      res.status,
      body?.error,
    );
  }
  return (await res.json()) as { ok: true };
}

export async function listHotels(
  apiBase: string,
  idToken: string,
): Promise<{ hotels: ReadonlyArray<HotelDTO> }> {
  const res = await bearer(`${apiBase}/api/organization/hotels`, idToken);
  if (!res.ok) throw new Error(`listHotels failed: ${res.status}`);
  return (await res.json()) as { hotels: ReadonlyArray<HotelDTO> };
}

export async function createHotel(
  apiBase: string,
  input: { name: string; city?: string; address?: string; phone?: string },
  idToken: string,
): Promise<{ id: string }> {
  const res = await bearer(`${apiBase}/api/organization/hotels`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`createHotel failed: ${res.status}`);
  return (await res.json()) as { id: string };
}

export async function updateHotel(
  apiBase: string,
  id: string,
  input: { name?: string; city?: string; address?: string; phone?: string },
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/organization/hotels/${id}`, idToken, {
    method: "PUT",
    body: input,
  });
  if (!res.ok) throw new Error(`updateHotel failed: ${res.status}`);
}

export async function deleteHotel(
  apiBase: string,
  id: string,
  opts: { force: boolean },
  idToken: string,
): Promise<void> {
  const suffix = opts.force ? "?force=true" : "";
  const res = await bearer(`${apiBase}/api/organization/hotels/${id}${suffix}`, idToken, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`deleteHotel failed: ${res.status}`);
}

export async function listDepartments(
  apiBase: string,
  hotelId: string,
  idToken: string,
): Promise<{ departments: ReadonlyArray<DepartmentDTO> }> {
  const res = await bearer(`${apiBase}/api/organization/hotels/${hotelId}/departments`, idToken);
  if (!res.ok) throw new Error(`listDepartments failed: ${res.status}`);
  return (await res.json()) as { departments: ReadonlyArray<DepartmentDTO> };
}

export async function createDepartment(
  apiBase: string,
  hotelId: string,
  input: {
    code: string;
    displayName: string;
    shifts: ReadonlyArray<"M" | "P" | "N" | "X">;
  },
  idToken: string,
): Promise<{ id: string }> {
  const res = await bearer(`${apiBase}/api/organization/hotels/${hotelId}/departments`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`createDepartment failed: ${res.status}`);
  return (await res.json()) as { id: string };
}

export async function deleteDepartment(
  apiBase: string,
  hotelId: string,
  code: string,
  idToken: string,
): Promise<void> {
  const res = await bearer(
    `${apiBase}/api/organization/hotels/${hotelId}/departments/${code}`,
    idToken,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`deleteDepartment failed: ${res.status}`);
}

/**
 * Updates an existing department (rename, edit shift slots, or reactivate a
 * disabled one). The `code` is the immutable doc id — pass it in the path, never
 * in the patch. Calls `PUT /hotels/:id/departments/:code`.
 */
export async function updateDepartment(
  apiBase: string,
  hotelId: string,
  code: string,
  patch: {
    displayName?: string;
    shifts?: ReadonlyArray<"M" | "P" | "N" | "X">;
    status?: "active" | "disabled";
    sample?: boolean;
  },
  idToken: string,
): Promise<void> {
  const res = await bearer(
    `${apiBase}/api/organization/hotels/${hotelId}/departments/${code}`,
    idToken,
    { method: "PUT", body: patch },
  );
  if (!res.ok) throw new Error(`updateDepartment failed: ${res.status}`);
}
